import { useAtom } from 'jotai';
import * as localState from './state';
import { CsvValidationException } from '@/components/Analysis/Idms/types';
import { BulkUploadPoisInput } from '@/__generated__/graphql';

export const useBulkUploadPoiActions = () => {
  const [bulkUploadModal, setBulkUploadModal] = useAtom(localState.bulkUploadModal);
  const [errors, setErrors] = useAtom(localState.errors);
  const [uploadInput, setUploadInput] = useAtom(localState.uploadInput);
  const [fileData, setFileData] = useAtom(localState.fileData);
  
  const getGetBulkModalState = () => bulkUploadModal;

  const openBulkUploadModal = () => setBulkUploadModal('upload');

  const closeAllModals = () => {
    setBulkUploadModal('closed');
    setErrors(undefined);
    setFileData(null);
  };

  const proceedToUploadConfirmation = (input: BulkUploadPoisInput) => {
    setUploadInput(input);
    setBulkUploadModal('confirm');
  };

  const cancelConfirmation = () => setBulkUploadModal('closed');

  const openErrorModal = (error: CsvValidationException) => {
    setErrors(error);
    
    // Map the error type to an acceptable modal state
    if (error.issue.type === 'header') {
      setBulkUploadModal('invalid_format');
    } else if (error.issue.type === 'filetype') {
      setBulkUploadModal('invalid_format');
    } else {
      setBulkUploadModal('validation');
    }
  };

  const getErrors = () => errors;
  const getUploadInput = () => uploadInput;
  const getFileData = () => fileData;

  const populateUploadInput = (input: BulkUploadPoisInput) => setUploadInput(input);
  
  const handleFileUpload = (file: File) => {
    // Only accept XLSX files
    if (!file.name.endsWith('.xlsx') && 
        file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      throw new Error('Only XLSX files are supported');
    }
    
    setFileData(file);
    return file;
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const prepareFileForUpload = async (file: File): Promise<string> => {
    // Convert XLSX file to base64 for backend processing
    return fileToBase64(file);
  };

  return {
    openBulkUploadModal,
    getGetBulkModalState,
    closeAllModals,
    proceedToUploadConfirmation,
    cancelConfirmation,
    openErrorModal,
    getErrors,
    getUploadInput,
    populateUploadInput,
    handleFileUpload,
    getFileData,
    prepareFileForUpload,
  };
};
